import React, { useState, useMemo, useEffect } from "react";
import SelectField from "../../../Forms/Input/SelectField";
import ButtonCustom from "../../../../pages/UiElements/ButtonCustom";
import { RawProfileOption } from "../../../../types/system/profiles";
import { useClientProfileSwitch } from "../../../../hooks/client/useClientProfileSwitch";
import ActiveProfileInfoCard from "./ActiveProfileInfoCard";

interface ClientProfileCardProps {
  profileOptions: RawProfileOption[];
  onProfileChange?: (profileId: number) => void;
}

const ClientProfileCard: React.FC<ClientProfileCardProps> = ({
  profileOptions,
  onProfileChange,
}) => {
  const { switchProfile, getActiveProfile } = useClientProfileSwitch();
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [activeProfile, setActiveProfile] = useState<{ id: number; name: string; code: string; description?: string } | null>(null);

  // 🔄 Convertir opciones
  const mappedOptions = useMemo(
    () =>
      profileOptions.map((opt: RawProfileOption) => ({
        value: opt.id,
        label: opt.name,
      })),
    [profileOptions]
  );

  // 📥 Obtener perfil activo una sola vez
  useEffect(() => {
    const fetchActive = async () => {
      const active = await getActiveProfile();
      if (active?.id) {
        setSelectedProfileId(active.id);
        setActiveProfile(active);
      }
    };
    fetchActive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectChange = (
    e:
      | React.ChangeEvent<{ name: string; value: number | string }>
      | { target: { name: string; value: number | string } }
  ) => {
    setSelectedProfileId(Number(e.target.value));
  };

  const handleConfirmChange = async () => {
    if (selectedProfileId !== null) {
      await switchProfile(selectedProfileId);
      onProfileChange?.(selectedProfileId);
      const updated = await getActiveProfile();
      if (updated?.id) setActiveProfile(updated);
    }
  };

  return (
    <div className="rounded-lg bg-white dark:bg-boxdark shadow p-6 w-full">
      <h2 className="text-gray-800 dark:text-gray-100 text-lg font-semibold mb-6">
        Cambiar Perfil de Cliente
      </h2>

      <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
        <div className="flex-1 w-full">
          <SelectField
            label=""
            name="profile"
            value={selectedProfileId ?? ""}
            onChange={handleSelectChange}
            options={mappedOptions}
            iconKey="list"
          />
        </div>

        <div className="w-full sm:w-auto">
          <ButtonCustom
            text="Cambiar"
            variant="success"
            onClick={handleConfirmChange}
            disabled={selectedProfileId === null}
            iconKey="change"
          />
        </div>
      </div>

      {/* 🧩 Componente reutilizable */}
      {activeProfile && (
        <ActiveProfileInfoCard
          name={activeProfile.name}
          code={activeProfile.code}
          description={activeProfile.description}
        />
      )}
    </div>
  );
};

export default ClientProfileCard;
